import { useEffect, useState } from "react";
import {
  getPushPermission,
  isPushSupported,
  pushNeedsPwaInstall,
  registerServiceWorker,
  subscribeToPush,
} from "../../../utils/pushNotifications";
import { isIos, isStandaloneMode, canShowInstallBanner } from "../../../utils/pwaUtils";
import s from "./clientApp.module.css";
import L from "../../../constants/baysportLabels";

export default function ClientInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const key = "baysport-pwa-dismiss";

  useEffect(() => {
    if (!canShowInstallBanner(key)) return undefined;
    if (isIos() && !isStandaloneMode()) {
      setShowIos(true);
      return undefined;
    }
    const onInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => window.removeEventListener("beforeinstallprompt", onInstall);
  }, [key]);

  const dismiss = () => {
    localStorage.setItem(key, "1");
    setDeferred(null);
    setShowIos(false);
  };

  if (showIos) {
    return (
      <div className={s.installBanner}>
        <div>
          <strong>Добавьте на главный экран</strong>
          <p>Safari → Поделиться → «На экран Домой»</p>
        </div>
        <button type="button" className={s.installDismiss} onClick={dismiss} aria-label="Закрыть">
          ×
        </button>
      </div>
    );
  }

  if (!deferred) return null;

  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  return (
    <div className={s.installBanner}>
      <div>
        <strong>Установить приложение</strong>
        <p>Добавьте {L.productName} на главный экран — быстрый доступ к клубу</p>
      </div>
      <div className={s.installActions}>
        <button type="button" className={s.installBtn} onClick={install}>
          Установить
        </button>
        <button type="button" className={s.installDismiss} onClick={dismiss} aria-label="Закрыть">
          ×
        </button>
      </div>
    </div>
  );
}

export function ClientPushPrompt() {
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(
    () => localStorage.getItem("baysport-push-dismiss") === "1",
  );
  const needsPwa = pushNeedsPwaInstall();

  useEffect(() => {
    registerServiceWorker();
    if (needsPwa) {
      setPermission("needs_pwa");
      return;
    }
    if (isPushSupported()) {
      getPushPermission().then(setPermission);
    } else {
      setPermission("unsupported");
    }
  }, [needsPwa]);

  if (hidden || permission === "unsupported" || permission === "granted") {
    return null;
  }

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setPermission("granted");
      localStorage.setItem("baysport-push-dismiss", "1");
    } catch {
      /* user denied */
    } finally {
      setBusy(false);
    }
  };

  if (permission === "needs_pwa") {
    return (
      <div className={s.pushBanner}>
        <span className={s.pushIcon}>🔔</span>
        <div className={s.pushText}>
          <strong>Уведомления на iPhone</strong>
          <p>Сначала добавьте приложение на главный экран (Поделиться → На экран «Домой»), затем включите push в профиле.</p>
        </div>
        <button
          type="button"
          className={s.installDismiss}
          onClick={() => {
            setHidden(true);
            localStorage.setItem("baysport-push-dismiss", "1");
          }}
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className={s.pushBanner}>
      <span className={s.pushIcon}>🔔</span>
      <div className={s.pushText}>
        <strong>Включите уведомления</strong>
        <p>Опросы, занятия и новости клуба — сразу на телефон</p>
      </div>
      <button type="button" className={s.pushBtn} onClick={enable} disabled={busy}>
        {busy ? "…" : "Включить"}
      </button>
      <button
        type="button"
        className={s.installDismiss}
        onClick={() => {
          setHidden(true);
          localStorage.setItem("baysport-push-dismiss", "1");
        }}
        aria-label="Закрыть"
      >
        ×
      </button>
    </div>
  );
}
